<template>
  <div>
    <guide-tour config-key="message.send.customList"/>
    <v-stepper v-model="step" vertical class="elevation-0">
      <v-stepper-step :complete="step > 1" step="1" :editable="step > 1">
        貼上手機號碼
        <small>單次 1～500 筆</small>
      </v-stepper-step>
      <v-stepper-content step="1">
        <v-card outlined class="mb-5 pa-5">
          <v-textarea
            v-model="phonesText"
            label="貼上手機號碼（一行一筆，也可用逗號或分號分隔）"
            placeholder="0912345678&#10;0922333444&#10;+886933555666"
            rows="8"
            outlined
            hide-details
          />
          <div class="mt-2">已貼上 {{ phoneEntryCount }} 筆</div>
        </v-card>
        <v-btn color="primary" @click="goToStep2">下一步</v-btn>
        <v-btn text class="ml-2" @click="phonesText = ''">清除內容</v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 2" step="2" :editable="step > 2">
        選擇發送管道
      </v-stepper-step>
      <v-stepper-content step="2">
        <v-card outlined class="mb-5 pa-5">
          <v-checkbox v-model="channels" label="LINE訊息 (已綁定LINE的患者)" value="line"
                      :disabled="hasSmsChannel" hide-details="auto" dense/>
          <v-checkbox v-model="channels" label="簡訊 (未綁定LINE的患者)" value="sms-unbound"
                      :disabled="hasLineChannel" hide-details="auto" dense/>
          <v-checkbox v-model="channels" label="簡訊 (已綁定LINE的患者)" value="sms-bound"
                      :disabled="hasLineChannel" hide-details="auto" dense/>
        </v-card>
        <v-btn color="primary" :disabled="!channels.length || loadingPreview" :loading="loadingPreview"
               @click="loadPreview()">下一步</v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 3" step="3" :editable="step > 3 && !!preview">
        確認發送名單(已選擇： {{ selectedPhones.length }}筆)
      </v-stepper-step>
      <v-stepper-content step="3">
        <v-row class="mb-2">
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-subtitle class="d-flex align-center">
                可發送名單
                <v-btn small color="primary" class="ml-auto" :disabled="!sendable.length"
                       @click="toggleAllPhones">{{ allSelected ? '取消全選' : '全選' }}</v-btn>
              </v-card-subtitle>
              <v-card-text>
                <v-text-field v-model.trim="search" label="搜尋手機或姓名" outlined dense hide-details/>
                <div class="overflow-y-auto mt-2" style="height: 220px;">
                  <v-checkbox v-for="item in filteredSendable" :key="item.phone"
                              :input-value="selectedPhones.includes(item.phone)"
                              :label="recipientLabel(item)" hide-details dense class="mt-1"
                              @change="togglePhone(item.phone)"/>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-subtitle class="d-flex align-center">
                已選清單
                <v-btn small color="primary" class="ml-auto" :disabled="!selectedPhones.length"
                       @click="selectedPhones = []">清除全部</v-btn>
              </v-card-subtitle>
              <v-card-text class="overflow-y-auto" style="height: 284px;">
                <template v-if="selectedPhones.length">
                  <v-chip v-for="phone in selectedPhones" :key="phone" class="ma-1" close outlined color="primary"
                          @click:close="removePhone(phone)">{{ phone }}</v-chip>
                </template>
                <div v-else class="text-center">尚無選擇</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <excluded-list :items="duplicateEntries" :title="`合併重複 ${preview ? preview.duplicates : 0} 筆`"
                       empty-text="尚無重複號碼"/>
        <excluded-list :items="excluded" :title="`系統已排除 ${excluded.length} 筆`" class="mb-5"/>
        <v-btn color="primary" :disabled="!preview || !preview.can_send" @click="confirmSelection">確認選擇</v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 4" step="4" :editable="step > 4">
        編輯訊息
        <small v-if="step === 4">{{ hasLineChannel ? '可上傳圖片與輸入文字，編輯後點擊「確定內容」' : '只能輸入文字訊息，編輯後點擊「確定內容」' }}</small>
      </v-stepper-step>
      <v-stepper-content step="4">
        <template v-if="hasLineChannel">
          <v-card outlined class="mb-5 pa-2">
            <v-row>
              <v-col cols="12" sm="4">
                <v-subheader class="pl-0">上傳想發送的圖片</v-subheader>
                <upload-img :key="uploadImgKey" v-model="lineImage" :aspect-ratio="1" class="mx-auto" style="max-width: 150px"/>
              </v-col>
              <v-col cols="12" sm="8">
                <v-subheader class="pl-0">輸入想說的話</v-subheader>
                <v-textarea v-model="lineText" outlined no-resize :messages="`已編輯 ${lineText.length} 字`"/>
              </v-col>
            </v-row>
          </v-card>
          <v-btn color="primary" @click="confirmContent">確定內容</v-btn>
        </template>
        <message-editor v-else :value.sync="smsContent" show-points @nextStep="step = 5"/>
      </v-stepper-content>
      <v-stepper-step step="5">確認發送</v-stepper-step>
      <v-stepper-content step="5">
        <v-card outlined class="mb-5 pa-5">
          <div>發送人數：{{ hasLineChannel ? 'LINE' : '簡訊' }} {{ selectedPhones.length }} 人</div>
          <div class="mt-4">發送內容：</div>
          <div class="text-pre-line">{{ hasLineChannel ? lineText : smsContent }}</div>
          <template v-if="hasLineChannel">
            <div class="mt-4">圖片內容：</div>
            <v-img v-if="lineImageUrl" :src="lineImageUrl" contain max-width="150" max-height="150"/>
            <div v-else>無</div>
          </template>
        </v-card>
        <excluded-list :items="sendExcluded" :title="sendExcludedTitle" class="mb-5"/>
        <v-btn color="primary" :loading="isPreparingSend"
               :disabled="isPreparingSend || isSending || (hasLineChannel && isCoolingDown)"
               @click="prepareSend">{{ hasLineChannel && isCoolingDown ? `請稍候 ${cooldownRemainingSeconds} 秒` : '確定，我要送出訊息' }}</v-btn>
      </v-stepper-content>
    </v-stepper>

    <confirm-dialog v-model="quotaDialog" title="訊息餘額" confirm-btn-text="確定發送"
                    confirm-btn-color="primary" cancel-btn-text="取消" :persistent="isSending"
                    @confirm="sendMessage" @cancel="invalidateSendPreview">
      <template v-slot:content>
        <v-card-text>
          <div class="text-pre-line text-center">
            <div style="font-size: 16px;">
              <template v-if="hasLineChannel">
                <template v-if="hasOwnChannel">目前剩餘LINE訊息約：<span class="text-decoration-underline">{{ lineQuota }}</span> 則<br></template>
                本次預計使用：<span class="text-decoration-underline">{{ sendUsage }}</span> 則
              </template>
              <template v-else>
                目前剩餘簡訊額度: <span class="text-decoration-underline">{{ smsQuota }}</span> 點<br>
                本次預計使用：<span class="text-decoration-underline">{{ smsUsagePoints }}</span> 點
              </template>
            </div>
            <div class="mt-4">
              <template v-if="hasLineChannel && hasOwnChannel">
                建議您前往 <a href="https://manager.line.biz/" target="_blank" style="color: #0044FF;">LINE後台</a> ，確認訊息量是否足夠與加購<br>
              </template>
              <template v-if="!hasLineChannel">
                前往左側 <router-link :to="{name: 'smsRefillLog'}" style="color: #0044FF;">點數儲值</router-link> ，確認簡訊點數是否足夠與加購<br>
              </template>
              若不足但選擇發出，將只有部分患者收到唷！
            </div>
          </div>
        </v-card-text>
      </template>
      <template v-slot:confirm>
        <v-btn color="primary" dark block :loading="isSending" :disabled="isSending" @click="sendMessage">確定發送</v-btn>
      </template>
    </confirm-dialog>
    <confirm-dialog v-model="successDialog" title="發送成功" :content="successContent"
                    confirm-btn-text="我知道了，關閉視窗" cancel-btn-text="" @confirm="successDialog = false"/>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { previewCustomListMessageAPI, sendCustomListMessageAPI, getMessageSettingAPI, getChannelQuotaAPI, getSmsQuotaAPI } from '@/api'
import GuideTour from '@/components/utils/GuideTour.vue'
import ExcludedList from '@/components/message/CustomList/ExcludedList.vue'
import UploadImg from '@/components/UploadImg.vue'
import MessageEditor from '@/components/message/send/MessageEditor.vue'
import ConfirmDialog from '@/components/utils/ConfirmDialog.vue'
import { countPhoneEntries, CUSTOM_LIST_MAX_ENTRIES } from '@/utils/customListPhones'
import { LINE_BROADCAST_COOLDOWN_MS, buildLineBroadcastCooldownKey, getCooldownRemainingSeconds,
  readLineBroadcastCooldown, writeLineBroadcastCooldown } from '@/utils/lineBroadcastCooldown'

export default {
  name: 'CustomListSendStep',
  components: { GuideTour, ExcludedList, UploadImg, MessageEditor, ConfirmDialog },
  data() {
    return {
      step: 1,
      phonesText: '',
      channels: [],
      preview: null,
      selectedPhones: [],
      search: '',
      loadingPreview: false,
      previewRequestId: 0,
      lineText: '',
      lineImage: null,
      lineImageUrl: null,
      uploadImgKey: 0,
      smsContent: '',
      sendPreview: null,
      sendPreviewRequestId: 0,
      sendPreviewFingerprint: '',
      preparedImage: null,
      isPreparingSend: false,
      isSending: false,
      quotaDialog: false,
      successDialog: false,
      successContent: '',
      lineQuota: null,
      smsQuota: null,
      cooldownExpiresAt: 0,
      cooldownRemainingSeconds: 0,
      cooldownTimer: null
    }
  },
  computed: {
    ...mapGetters(['axiosParamData', 'ownChannelLoaded', 'hasOwnChannel']),
    phoneEntryCount() { return countPhoneEntries(this.phonesText) },
    hasLineChannel() { return this.channels.includes('line') },
    hasSmsChannel() { return this.channels.includes('sms-unbound') || this.channels.includes('sms-bound') },
    sendable() { return this.preview && Array.isArray(this.preview.sendable) ? this.preview.sendable : [] },
    excluded() { return this.preview && Array.isArray(this.preview.excluded) ? this.preview.excluded : [] },
    duplicateEntries() {
      return this.preview && Array.isArray(this.preview.duplicate_entries) ? this.preview.duplicate_entries : []
    },
    filteredSendable() {
      const query = this.search.trim().toLowerCase()
      return this.sendable.filter(item => !query || item.phone.includes(query) ||
        (item.patient_names || []).some(name => name.toLowerCase().includes(query)))
    },
    allSelected() {
      return this.sendable.length > 0 && this.selectedPhones.length === this.sendable.length
    },
    manuallyCancelled() {
      return this.sendable.map(item => item.phone).filter(phone => !this.selectedPhones.includes(phone))
    },
    sendExcluded() {
      return [...this.excluded, ...this.manuallyCancelled.map(value => ({ value, reason: 'manual' }))]
    },
    sendExcludedTitle() {
      return `已排除 ${this.excluded.length} 筆${this.manuallyCancelled.length ? `、手動取消 ${this.manuallyCancelled.length} 筆` : ''}`
    },
    sendTargetKey() {
      return JSON.stringify([this.selectedPhones, this.channels, this.phonesText, this.lineText, this.smsContent])
    },
    sendUsage() {
      return this.sendPreview ? Number(this.sendPreview.usage) : 0
    },
    smsUsagePoints() {
      return Math.ceil(this.smsContent.length / 70) * this.sendUsage
    },
    isCoolingDown() {
      return this.cooldownRemainingSeconds > 0
    }
  },
  watch: {
    phonesText() {
      this.invalidatePreview()
      if (this.step > 2) this.step = 1
    },
    channels: {
      deep: true,
      handler() {
        this.invalidatePreview()
        if (this.step > 2) this.step = 2
      }
    },
    sendTargetKey() {
      this.invalidateSendPreview()
    },
    lineImage(value) {
      if (this.lineImageUrl) URL.revokeObjectURL(this.lineImageUrl)
      this.lineImageUrl = value ? URL.createObjectURL(value) : null
      this.invalidateSendPreview()
    },
    quotaDialog(value) {
      if (!value) this.invalidateSendPreview()
    }
  },
  methods: {
    ...mapMutations(['setOwnChannel']),
    invalidatePreview() {
      this.invalidateSendPreview()
      this.previewRequestId++
      this.preview = null
      this.selectedPhones = []
      this.loadingPreview = false
    },
    goToStep2() {
      if (!this.phoneEntryCount) {
        this.$notify.warning('請先貼上手機號碼')
      } else if (this.phoneEntryCount > CUSTOM_LIST_MAX_ENTRIES) {
        this.$notify.warning('請貼上 1～500 筆手機號碼')
      } else {
        this.step = 2
      }
    },
    async loadPreview(manuallyCancelled = []) {
      await this.$nextTick()
      if (!this.channels.length || this.loadingPreview) return
      this.invalidatePreview()
      this.loadingPreview = true
      const requestId = this.previewRequestId
      try {
        const response = await previewCustomListMessageAPI({
          ...this.axiosParamData,
          phones: this.phonesText,
          channels: [...this.channels]
        })
        if (requestId !== this.previewRequestId) return
        this.preview = response.data
        const cancelled = Array.isArray(manuallyCancelled) ? manuallyCancelled : []
        this.selectedPhones = this.sendable.map(item => item.phone).filter(phone => !cancelled.includes(phone))
        this.search = ''
        this.step = 3
        if (Number(this.preview.sendable_count) === 0) {
          this.$notify.warning('沒有可發送的號碼，請檢查貼上的內容或改選發送管道')
        } else if (this.hasLineChannel && this.preview.can_send === false) {
          this.$notify.warning(this.preview.messages)
        }
      } catch (error) {
        if (requestId === this.previewRequestId) {
          this.$notify.alert(error.response && error.response.status === 422 && error.response.data
            ? error.response.data.messages : '無法取得發送名單，請稍後再試一次')
        }
      } finally {
        if (requestId === this.previewRequestId) this.loadingPreview = false
      }
    },
    recipientLabel(item) {
      const names = (item.patient_names || []).join('、')
      return names ? `${item.phone} ${names}` : item.phone
    },
    togglePhone(phone) {
      if (this.selectedPhones.includes(phone)) {
        this.removePhone(phone)
      } else {
        this.selectedPhones = this.sendable.map(item => item.phone)
          .filter(value => value === phone || this.selectedPhones.includes(value))
      }
    },
    removePhone(phone) {
      this.selectedPhones = this.selectedPhones.filter(value => value !== phone)
    },
    toggleAllPhones() {
      this.selectedPhones = this.allSelected ? [] : this.sendable.map(item => item.phone)
    },
    confirmSelection() {
      if (!this.preview || !this.preview.can_send) return
      if (!this.selectedPhones.length) {
        this.$notify.warning('請至少選擇一位發送對象')
        return
      }
      this.step = 4
    },
    confirmContent() {
      if (!this.lineText.trim() && !this.lineImage) {
        this.$notify.warning('請輸入訊息內容或上傳圖片')
        return
      }
      this.step = 5
    },
    invalidateSendPreview() {
      this.sendPreviewRequestId++
      this.sendPreview = null
      this.sendPreviewFingerprint = ''
      this.preparedImage = null
      this.quotaDialog = false
    },
    getCooldownStorageKey() {
      return buildLineBroadcastCooldownKey(this.axiosParamData)
    },
    stopCooldownTimer() {
      clearInterval(this.cooldownTimer)
      this.cooldownTimer = null
    },
    syncCooldownState() {
      this.cooldownRemainingSeconds = getCooldownRemainingSeconds(this.cooldownExpiresAt)
      if (this.cooldownRemainingSeconds > 0) return
      const stored = readLineBroadcastCooldown(this.getCooldownStorageKey())
      if (stored > this.cooldownExpiresAt) {
        this.cooldownExpiresAt = stored
        this.cooldownRemainingSeconds = getCooldownRemainingSeconds(stored)
        return
      }
      this.cooldownExpiresAt = 0
      this.stopCooldownTimer()
    },
    startCooldownTimer() {
      this.stopCooldownTimer()
      this.syncCooldownState()
      if (this.cooldownRemainingSeconds) this.cooldownTimer = setInterval(() => this.syncCooldownState(), 1000)
    },
    restoreCooldown() {
      this.cooldownExpiresAt = readLineBroadcastCooldown(this.getCooldownStorageKey())
      this.startCooldownTimer()
    },
    refreshCooldownFromStorage() {
      const stored = readLineBroadcastCooldown(this.getCooldownStorageKey())
      if (stored > this.cooldownExpiresAt) {
        this.cooldownExpiresAt = stored
        this.startCooldownTimer()
      } else this.syncCooldownState()
    },
    handleCooldownStorage(event) {
      if (event.key === this.getCooldownStorageKey()) this.refreshCooldownFromStorage()
    },
    alertCooldown() {
      this.$notify.alert(`群發訊息需間隔一分鐘發送，請稍候 ${this.cooldownRemainingSeconds} 秒後再試一次`)
    },
    activateCooldown() {
      this.cooldownExpiresAt = Date.now() + LINE_BROADCAST_COOLDOWN_MS
      writeLineBroadcastCooldown(this.getCooldownStorageKey(), this.cooldownExpiresAt)
      this.startCooldownTimer()
    },
    async prepareSend() {
      await this.$nextTick()
      if (this.isSending || this.isPreparingSend) return
      if (this.hasLineChannel) {
        this.refreshCooldownFromStorage()
        if (this.isCoolingDown) {
          this.alertCooldown()
          return
        }
      }
      if (!this.selectedPhones.length) {
        this.$notify.warning('請至少選擇一位發送對象')
        return
      }
      if (this.hasLineChannel ? !this.lineText.trim() && !this.lineImage : !this.smsContent.trim()) {
        this.$notify.warning(this.hasLineChannel ? '請輸入訊息內容或上傳圖片' : '請輸入訊息內容')
        return
      }
      this.invalidateSendPreview()
      this.isPreparingSend = true
      const requestId = this.sendPreviewRequestId
      const fingerprint = this.sendTargetKey
      const image = this.lineImage
      const isCurrent = () => requestId === this.sendPreviewRequestId && fingerprint === this.sendTargetKey && image === this.lineImage
      let checkingQuota = false
      try {
        const response = await previewCustomListMessageAPI({
          ...this.axiosParamData, phones: this.selectedPhones.join('\n'), channels: [...this.channels]
        })
        if (!isCurrent()) return
        const result = response.data
        if (Number(result.sendable_count) !== this.selectedPhones.length || result.can_send !== true) {
          this.$notify.alert(result.can_send === false && result.messages
            ? result.messages : '名單已變動，請重新確認發送名單')
          const cancelled = this.manuallyCancelled
          this.step = 3
          await this.loadPreview(cancelled)
          return
        }
        checkingQuota = true
        if (this.hasLineChannel) {
          if (!this.ownChannelLoaded) {
            const setting = await getMessageSettingAPI(this.axiosParamData)
            if (!isCurrent()) return
            const ownChannel = setting.data && setting.data.own_channel
            if (![true, false, 0, 1, '0', '1'].includes(ownChannel)) throw new Error('Invalid own_channel')
            this.setOwnChannel(Boolean(Number(ownChannel)))
          }
          if (this.hasOwnChannel) {
            const quota = await getChannelQuotaAPI(this.axiosParamData)
            if (!isCurrent()) return
            this.lineQuota = Number(quota.data.quota) - Number(quota.data.total_usage)
          }
        } else {
          const quota = await getSmsQuotaAPI(this.axiosParamData)
          if (!isCurrent()) return
          this.smsQuota = Number(quota.data.quota)
          if (this.smsQuota <= 0) {
            this.$notify.alert('點數餘額不足，請聯繫客服')
            return
          }
        }
        if (!isCurrent()) return
        this.sendPreview = result
        this.sendPreviewFingerprint = fingerprint
        this.preparedImage = image
        this.quotaDialog = true
      } catch (error) {
        if (isCurrent()) this.$notify.alert(checkingQuota
          ? '無法取得訊息餘額，請稍後再試一次'
          : error.response && error.response.status === 422 && error.response.data && error.response.data.messages
            ? error.response.data.messages : '無法取得發送名單，請稍後再試一次')
      } finally {
        this.isPreparingSend = false
      }
    },
    buildSendPayload() {
      const data = {
        ...this.axiosParamData,
        phones: this.selectedPhones.join('\n'),
        channels: this.channels.join(','),
        content: this.hasLineChannel ? this.lineText : this.smsContent,
        expected_recipient_count: this.selectedPhones.length
      }
      if (!this.hasLineChannel || !this.lineImage) return data
      const formData = new FormData()
      for (const [key, value] of Object.entries({ ...data, image: this.lineImage })) {
        if (value !== null && typeof value !== 'undefined') formData.append(key, value)
      }
      return formData
    },
    async sendMessage() {
      if (!this.quotaDialog || !this.sendPreview || this.isSending) return
      if (this.sendPreviewFingerprint !== this.sendTargetKey || this.preparedImage !== this.lineImage) {
        this.invalidateSendPreview()
        return
      }
      this.isSending = true
      let retry = false
      try {
        const response = await sendCustomListMessageAPI(this.buildSendPayload())
        if (String(response.data.status) === '0') {
          throw { response: { status: 422, data: response.data } }
        }
        if (String(response.data.status) !== '1') throw new Error('Invalid send status')
        this.quotaDialog = false
        if (this.hasLineChannel) this.activateCooldown()
        this.successContent = `預計數分鐘內發送完畢\n可至左側「發送紀錄」的「${this.hasLineChannel ? 'LINE 群發記錄' : '簡訊群發記錄'}」查詢`
        this.successDialog = true
        this.resetForm()
      } catch (error) {
        const status = error.response && error.response.status
        const messages = error.response && error.response.data && error.response.data.messages
        if (status === 409) {
          this.$notify.alert(messages || '收件人數已變更，請重新確認後發出')
          retry = true
        } else if (status === 422) {
          this.$notify.alert(messages || '發送條件不符，請調整後再試一次')
        } else if (status === 429 && this.hasLineChannel) {
          // 與前端冷卻同一規則，但後端以整個帳號計算（同診所其他人剛發過也會擋）：啟動冷卻並沿用同一句警示
          this.activateCooldown()
          this.alertCooldown()
        } else if (status === 429) {
          this.$notify.alert(messages || '發送過於頻繁，請稍候再試一次')
        } else this.$notify.alert('發送失敗，請稍後再試一次')
        this.invalidateSendPreview()
      } finally {
        this.isSending = false
      }
      if (retry) await this.prepareSend()
    },
    resetForm() {
      this.step = 1
      this.phonesText = ''
      this.channels = []
      this.preview = null
      this.selectedPhones = []
      this.search = ''
      this.lineText = ''
      this.lineImage = null
      // UploadImg 不監聽 value，需重建才會清掉舊圖
      this.uploadImgKey++
      this.smsContent = ''
      this.lineQuota = null
      this.smsQuota = null
      this.loadingPreview = false
      this.invalidateSendPreview()
    }
  },
  mounted() {
    this.restoreCooldown()
    window.addEventListener('storage', this.handleCooldownStorage)
  },
  beforeDestroy() {
    this.stopCooldownTimer()
    window.removeEventListener('storage', this.handleCooldownStorage)
    if (this.lineImageUrl) URL.revokeObjectURL(this.lineImageUrl)
  }
}
</script>
